import { FC, ReactNode, useContext, useState } from 'react';

import { Button, SxProps, Theme } from '@mui/material';
import logger from 'logger';

import ConfigContext from 'src/context/config';

import { loadZendeskScript } from './zendeskScript';
import { Noticeable } from '@honeybadger-io/core/build/src/types';

export interface ZendeskLoaderProps {
  children?: ReactNode;
  loadingIndicator?: ReactNode | string;
  hideWhenOpen?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Resolves once the Zendesk Messaging widget is fully initialized.
 *
 * snippet.js loads additional assets (web-widget-main-*.js) after download,
 * and window.zE is only defined once those assets finish loading. We poll
 * for window.zE to exist, then use the official zE(callback) ready pattern
 * to confirm the widget is fully initialized before resolving.
 *
 *
 *  Both `interval` and `timer` are assigned synchronously before any callback fires (the first interval runs 100ms later).
 * If the interval resolves first, it clears the timer. If the timer fires first
 *   (10s timeout), it clears the interval. Either path cleans up both.
 */
function onZendeskReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window.zE) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      zE(() => resolve());
      return;
    }

    const interval = setInterval(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (window.zE) {
        clearInterval(interval);
        clearTimeout(timer);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        zE(() => resolve());
      }
    }, 100);

    const timer = setTimeout(() => {
      clearInterval(interval);
      reject(new Error('Zendesk unable to load'));
    }, 10000);
  });
}

let scriptLoadPromise: Promise<void> | null = null;

/**
 * https://developer.zendesk.com/api-reference/widget-messaging/web/core/
 */
const ZendeskLoader: FC<ZendeskLoaderProps> = ({
  children,
  loadingIndicator = 'Loading…',
  hideWhenOpen = false,
  sx = {},
}) => {
  const config = useContext(ConfigContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);

  const addWidgetEventHandlers = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('messenger:on', 'close', () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      zE('messenger', 'hide');
      setIsWidgetOpen(false);
    });
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('messenger:on', 'open', () => {
      setIsLoading(false);
      setIsWidgetOpen(true);
    });
  };

  const openWidget = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('messenger', 'show');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('messenger', 'open');
  };

  const loadZendesk = async () => {
    setIsLoading(true);

    try {
      if (!scriptLoadPromise) {
        // Set the zendesk key from the tenant config if it exists, otherwise fallback to the environment variable.The zendesk key env variable is defaulted to use the Mealogic channel key.
        const zendeskKey =
          config?.clientSideKeys?.zendeskKey ??
          process.env.NEXT_PUBLIC_ZENDESK_KEY!;

        scriptLoadPromise = loadZendeskScript(zendeskKey);
      }

      await scriptLoadPromise;
      await onZendeskReady();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // Adds a tag to the conversation with the domain.
      zE('messenger:set', 'conversationTags', [
        `submitted_from_${window.location.href}`,
      ]);

      addWidgetEventHandlers();
      openWidget();
    } catch (error) {
      logger.error({
        msg: 'Failed to load Zendesk',
        noticable: error as Noticeable,
      });
      setIsLoading(false);
    }
  };

  const renderZendeskLoader = () => (
    <Button
      sx={sx}
      variant="text"
      onClick={loadZendesk}
      disabled={isLoading || isWidgetOpen}
    >
      {isLoading ? loadingIndicator : children}
    </Button>
  );
  return hideWhenOpen && isWidgetOpen ? null : renderZendeskLoader();
};

export default ZendeskLoader;
